import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import { Carousel } from "react-responsive-carousel";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import Div from "@jumbo/shared/Div";
import { SET_ROLE } from "app/redux/actions/actionTypes";
import { passwordFieldSx } from "app/utils/constants/passwordFieldStyles";
import { CLIENT_WITH_IDP, tenantIdpMappings } from "app/utils/constants/tenantIdpMappings";
import { axios_public } from "../../../axios";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-responsive-carousel/lib/styles/carousel.min.css";

const validationSchema = yup.object({
    email: yup.string("Enter your email").email("Enter a valid email").required("Email is required"),
    password: yup.string("Enter your password").required("Password is required")
});

function getTenantName() {
    const hostname = window.location.hostname;
    // Remove protocol (http:// or https://) if present
    const cleanOrigin = hostname.replace(/^(https?:\/\/)/, "");
    const parts = cleanOrigin.split(".");
    let tenant;
    if (parts.length >= 2) {
        tenant = parts[0];
    } else {
        tenant = process.env.REACT_APP_LOCAL_TENANT || "trexo-dev"; // Fallback to a default tenant if not found
    }
    return tenant;
}

const TenantLogin = () => {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sendLinkLoader, setSendLinkLoader] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const subdomain = window.location.hostname.split(".")[0];
    const tenantIdpInfo = tenantIdpMappings[subdomain] || null;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (localStorage.getItem("TREXO_TENANT_AUTH_TOKEN")) {
            navigate("/dashboard");
        } else {
            navigate("/login");
        }
    }, []);

    const carouselItems = [
        { imgUrl: "/images/June.svg", alt: "June" },
        { imgUrl: "/images/Docgen.svg", alt: "Docgen" },
        { imgUrl: "/images/DRM.svg", alt: "DRM" },
        { imgUrl: "/images/TaskManager.svg", alt: "Task Manager" },
        { imgUrl: "/images/Collab.svg", alt: "Collaboration-space" },
        { imgUrl: "/images/ADIM.svg", alt: "ADIM" },
    ];

    const onSignIn = (email, password) => {
        setLoading(true);
        axios_public
            .post("/tenantAPI/user/login", { email: email.trim(), password,rememberMe })
            .then(res => {
                if (res.status === 200) {
                    setLoading(false);
                    localStorage.setItem("tenant_username", res.data.fname + " " + res.data.lname);

                    localStorage.setItem("TENANT_NAME", res.data.tenantName);
                    localStorage.setItem("TENANT_USER_ID", res.data.id);
                    localStorage.setItem("TENANT_USER_MAIL", res.data.mail);
                    localStorage.setItem("TREXO_TENANT_AUTH_TOKEN", res.data.token);
                    localStorage.setItem("TENANT_ROLE", res.data.role);
                    dispatch({ type: SET_ROLE, payload: res.data.role });
                    localStorage.setItem("ICON", res.data.icon);
                    const sessionId = uuidv4();
                    sessionStorage.setItem("sessionId", sessionId);

                    const latestPath = localStorage.getItem("latestPath");
                    if (latestPath?.includes("collaboration-space") || latestPath?.includes("/oa/")) {
                        navigate(latestPath);
                    } else {
                        navigate("/dashboard");
                    }
                } else {
                    setLoading(false);
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const style = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 400,
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4
    };

    const handleClose = () => {
        if (!sendLinkLoader) {
            setOpen(false)
        }
    };
    const handleClick = () => setOpen(!open);

    const handleLink = e => {
        e.preventDefault();
        setSendLinkLoader(true);

        axios_public
            .post("/tenantAPI/user/forgotpassword", { email: email.trim() })
            .then(() => {
                toast.success(`Link sent to ${email}`);
            })
            .catch(() => {
            })
            .finally(() => {
                setSendLinkLoader(false);
                setOpen(false);
                setEmail("");
            });
    };
    const tenantName = getTenantName().toLowerCase();
    if (tenantName === "crowell") {
        return (
            <Box sx={{
                display: 'flex', p: 3, flexDirection: "column", justifyContent: 'center', alignItems: 'center',
                p: 5,
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.08)', // softer spread
                borderRadius: 1,
            }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-around",
                        minHeight: 300,
                        bgcolor: "#fff",
                        borderRadius: 2,
                        py: 5,
                        px: 5,
                    }}
                >
                    <Box
                        component="img"
                        src="/images/crowell.png"
                        alt="Crowell Logo"
                        sx={{ width: 300, mb: 2 }}
                    />
                </Box>
                <Typography variant="h1" sx={{ textAlign: "center", color: "#222", fontWeight: 600, fontSize: "2.1rem" }} pb={2}>
                    Family Numbering System
                </Typography>
                <Button variant="contained" size="large" sx={{ py: 1, px: 6 }} onClick={() => {
                    window.location.href = tenantIdpMappings[process.env.REACT_APP_NUMBER_GENERATOR_TENANT]?.loginUrl
                }}>
                    Login with {tenantIdpMappings[process.env.REACT_APP_NUMBER_GENERATOR_TENANT]?.idpName}
                </Button>

            </Box>
        );
    }
    return (
        <Div
            sx={{
                backgroundSize: "cover",
                minHeight: "100vh",
                minWidth: "100%"
            }}
        >
            <Grid container sx={{ height: "100%" }} spacing={2}>
                <Grid item xs={5} sm={5} md={5} lg={5}>
                    <Box sx={{ margin: "25% 10% 10% 20%", display: "flex", justifyContent: "center", alignItems: "center", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
                        <Carousel showArrows={false} showStatus={false} autoPlay infiniteLoop showThumbs={false} verticalSwipe={"natural"}>
                            {carouselItems.length > 0 &&
                                carouselItems.map((p, i) => (
                                    <Paper key={i} style={{ backgroundColor: "#fff", paddingBottom: "15%", paddingTop: "5%" }}>
                                        <img style={{ width: "70%", aspectRatio: "1/1", objectFit: "contain" }} src={p.imgUrl} alt={p.alt} />
                                    </Paper>
                                ))}
                        </Carousel>

                    </Box>
                </Grid>

                <Grid item xs={7} sm={7} md={7} lg={7}>
                    <Box sx={{ margin: "20% 10% 10% 20%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Card
                            sx={{
                                width: "55%",
                                background: "#FFF"
                            }}
                        >
                            <CardContent sx={{ flex: 1, p: 4 }}>
                                <Div sx={{ mt: "auto", mb: 4 }}>
                                    <Link href="#" underline="none">
                                        <Box component="img" src={"/images/logo.png"} alt="" sx={{ width: ["100%", "90%", "80%"] }} />
                                    </Link>
                                </Div>
                                <Formik
                                    validateOnChange={true}
                                    initialValues={{
                                        email: "",
                                        password: ""
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={(data, { setSubmitting }) => {
                                        setSubmitting(true);
                                        onSignIn(data.email, data.password,rememberMe);
                                        setSubmitting(false);
                                    }}
                                >
                                    {() => (
                                        <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                                            <Div sx={{ mt: 1, mb: 3 }}>
                                                <JumboTextField
                                                    fullWidth
                                                    name="email"
                                                    placeholder="Email"
                                                />
                                            </Div>
                                            <Div sx={{ mt: 1, mb: 2 }}>
                                               <JumboTextField
                                                fullWidth name="password" placeholder="Password" type={showPassword ? "text" : "password"}
                                                sx={passwordFieldSx}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                type="button"
                                                                edge="end"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => setShowPassword(prev => !prev)}
                                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                                title={showPassword ? "Hide password" : "Show password"}
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            </Div>
                                            <Div sx={{ mb: 2 }}>
                                                <FormControlLabel control={ <Checkbox
                                                    checked={rememberMe}
                                                    onChange={(e) => {
                                                        console.log("Checkbox:", e.target.checked);
                                                        setRememberMe(e.target.checked);
                                                    }}
                                                />} label="Remember me" />
                                            </Div>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                <Link style={{ cursor: "pointer" }} color={"inherit"} underline={"none"} onClick={handleClick}>
                                                    Forgot your password? Recover Now
                                                </Link>
                                            </Typography>

                                            <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                                                <Div sx={style}>
                                                    <Typography id="modal-modal-title" variant="h4" component="h2">
                                                        <TextField label="Email" value={email} variant="outlined" fullWidth onChange={e => setEmail(e.target.value)} />
                                                    </Typography>

                                                    <Typography variant="body1" sx={{ mb: 2 }} style={{ fontSize: "small", color: "#010160" }}>
                                                        A password reset link will be sent to the above email
                                                    </Typography>

                                                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                                                        <Button type="submit" variant="contained" color="primary" onClick={handleLink} disabled={sendLinkLoader}>
                                                            {sendLinkLoader ? <CircularProgress size={25} sx={{ color: "blue" }} /> : <span>Send Link</span>}
                                                        </Button>
                                                    </Typography>
                                                </Div>
                                            </Modal>

                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <LoadingButton fullWidth type="submit" variant="contained" size="large" sx={{ mb: 3 }} disabled={loading}>
                                                    {loading ? <CircularProgress size={25} sx={{ color: "blue" }} /> : <span>Sign In</span>}
                                                </LoadingButton>

                                            </Box>
                                        </Form>
                                    )}
                                </Formik>
                                {subdomain && CLIENT_WITH_IDP.includes(subdomain) ? (
                                    <Button fullWidth variant="contained" size="large" sx={{ mb: 3 }} onClick={() => {
                                        window.location.href = tenantIdpInfo?.loginUrl
                                    }}>
                                        Login with {tenantIdpInfo?.idpName}
                                    </Button>
                                ) : null}
                            </CardContent>
                        </Card>
                    </Box>
                </Grid>
            </Grid>
        </Div>
    );
};

export default TenantLogin;